import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import {
  DATE_RANGE_PRESET_OPTIONS,
  getDateRangePresetLabel,
  type DateRangePreset,
} from '../utils/dateRangePresets';
import { getDropdownPosition } from '../utils/dropdownPosition';

interface DateRangeSelectProps {
  preset: DateRangePreset;
  startDate: string;
  endDate: string;
  onPresetChange: (preset: DateRangePreset) => void;
  onCustomRangeChange: (startDate: string, endDate: string) => void;
  label?: string;
  className?: string;
}

const DateRangeSelect = ({
  preset,
  startDate,
  endDate,
  onPresetChange,
  onCustomRangeChange,
  label = 'Date',
  className = '',
}: DateRangeSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    setDropdownStyle(getDropdownPosition(triggerRef.current.getBoundingClientRect()));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updateDropdownPosition();
    requestAnimationFrame(updateDropdownPosition);

    const handleReposition = () => updateDropdownPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  const displayLabel = getDateRangePresetLabel(preset, startDate, endDate);

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden min-w-[220px]"
    >
      <ul className="max-h-72 overflow-y-auto py-1">
        {DATE_RANGE_PRESET_OPTIONS.map((option) => {
          const selected = preset === option.value;
          return (
            <li key={option.value}>
              <button
                type="button"
                onClick={() => {
                  onPresetChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-gray-50 ${
                  selected ? 'text-emerald-700 font-medium' : 'text-gray-800'
                }`}
              >
                <span className="w-4 shrink-0">
                  {selected && <Check className="h-4 w-4 text-emerald-600" />}
                </span>
                <span>{option.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div ref={triggerRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          className={`w-full flex items-center justify-between px-4 py-2 bg-white border rounded-lg text-sm text-left transition-colors ${
            isOpen
              ? 'border-emerald-500 ring-1 ring-emerald-500'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <span className="truncate text-gray-900">{displayLabel}</span>
          <ChevronDown
            className={`h-4 w-4 text-gray-500 shrink-0 ml-2 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      </div>

      {dropdown && createPortal(dropdown, document.body)}

      {preset === 'custom' && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => onCustomRangeChange(e.target.value, endDate)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => onCustomRangeChange(startDate, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelect;
